import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, CheckCircle, Warning, Sparkle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/common";
import { api, errMsg, money } from "@/lib/api";

export default function ReceiptScanDialog({ open, onOpenChange, accounts = [], products = [] }) {
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [supplierId, setSupplierId] = useState("");
  const [lines, setLines] = useState([]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setRes(null);
  };

  const scan = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/invoices/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setRes(data);
      setSupplierId(data.supplier.match_id || "__new__");
      setLines(data.items.map((it) => ({ ...it, choice: it.product_id || "__new__" })));
      toast.success(`${data.items.length} kalem okundu`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const apply = async () => {
    setBusy(true);
    try {
      let accountId = supplierId;
      if (supplierId === "__new__") {
        const s = res.supplier;
        const { data } = await api.post("/accounts", { name: s.name || "Yeni Tedarikçi", type: "supplier", tax_no: s.tax_no || "", tax_office: s.tax_office || "", phone: s.phone || "", address: s.address || "" });
        accountId = data.id;
      }
      const items = [];
      for (const l of lines) {
        if (l.choice === "__skip__") continue;
        let pid = l.choice;
        if (pid === "__new__") {
          const { data } = await api.post("/products", { name: l.name, unit: l.unit || "Adet", vat_rate: l.vat_rate, purchase_price: l.price, sale_price: l.price });
          pid = data.id;
        }
        items.push({ product_id: pid, qty: l.qty, price: l.price, vat_rate: l.vat_rate, discount: 0 });
      }
      onOpenChange(false);
      nav("/faturalar/yeni?tur=purchase", { state: { draft: { account_id: accountId, date: res.date || undefined, currency: res.currency || "TRY", notes: res.document_no ? `Belge No: ${res.document_no}` : "", items, receipt_file_id: res.receipt_file_id } } });
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-[6px]" data-testid="receipt-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Camera weight="fill" className="text-[#fdba74]" /> Fiş / Fatura Fotoğrafından Alış Faturası</DialogTitle>
          <DialogDescription>Fotoğrafı yükleyin; yapay zekâ tedarikçi, ürün ve tutarları okuyup mevcut kayıtlarınızla eşleştirir. Taslağı kontrol edip kaydedersiniz.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-[220px_1fr]">
          <div>
            <label className="flex aspect-[3/4] cursor-pointer items-center justify-center overflow-hidden border-2 border-dashed border-border bg-muted/40 text-center text-xs text-muted-foreground hover:border-teal-400" data-testid="receipt-dropzone">
              {preview ? <img src={preview} alt="Fiş" className="h-full w-full object-contain" /> : <span className="px-4">Fotoğraf seçmek için tıklayın<br />(JPEG/PNG/WEBP, max 8 MB)</span>}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="receipt-file-input" />
            </label>
            <Button onClick={scan} disabled={!file || busy} className="mt-3 w-full rounded-[4px]" data-testid="receipt-scan-button">
              <Sparkle weight="fill" className="mr-1" /> {busy && !res ? "Okunuyor…" : "Oku ve Eşleştir"}
            </Button>
          </div>
          <div className="min-h-[200px] text-sm">
            {!res ? <p className="text-muted-foreground">Henüz okuma yapılmadı.</p> : (
              <div className="space-y-4" data-testid="receipt-result">
                <div className="border p-3">
                  <div className="label-eyebrow">Tedarikçi</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className="font-semibold" data-testid="receipt-supplier-name">{res.supplier.name || "—"}</span>
                    {res.supplier.tax_no && <span className="text-xs text-muted-foreground">VKN {res.supplier.tax_no}</span>}
                    {res.supplier.match_id ? <span className="inline-flex items-center gap-1 text-xs text-teal-700"><CheckCircle weight="fill" /> Eşleşti: {res.supplier.match_name}</span>
                      : <span className="inline-flex items-center gap-1 text-xs text-orange-700"><Warning weight="fill" /> Kayıtlarda bulunamadı</span>}
                  </div>
                  <NativeSelect value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="mt-2" data-testid="receipt-supplier-select">
                    <option value="__new__">➕ Yeni tedarikçi olarak oluştur</option>
                    {accounts.filter((a) => a.type !== "customer").map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </NativeSelect>
                </div>
                <div className="border">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-muted/60 text-left"><th className="p-2">Okunan kalem</th><th className="p-2 text-right">Miktar</th><th className="p-2 text-right">Fiyat</th><th className="p-2">Ürün eşleşmesi</th></tr></thead>
                    <tbody>
                      {lines.map((l, i) => (
                        <tr key={i} className="border-t" data-testid={`receipt-line-${i}`}>
                          <td className="p-2">{l.name}<div className="text-[10px] text-muted-foreground">KDV %{l.vat_rate}</div></td>
                          <td className="p-2 text-right font-num">{l.qty} {l.unit}</td>
                          <td className="p-2 text-right font-num">{money(l.price, res.currency || "TRY")}</td>
                          <td className="p-2">
                            <NativeSelect value={l.choice} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, choice: e.target.value } : x)))} data-testid={`receipt-line-select-${i}`}>
                              <option value="__new__">➕ Yeni ürün oluştur</option>
                              <option value="__skip__">Atla</option>
                              {(l.candidates.length ? l.candidates : []).map((c) => <option key={c.id} value={c.id}>{c.name} (%{Math.round(c.score * 100)})</option>)}
                              <optgroup label="Tüm ürünler">{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>
                            </NativeSelect>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Okunan toplam: <b className="font-num">{money(res.total || 0, res.currency || "TRY")}</b>{res.date ? ` · ${res.date}` : ""}{res.confidence != null ? ` · güven %${Math.round(res.confidence * 100)}` : ""}</span>
                  <Button onClick={apply} disabled={busy || !lines.some((l) => l.choice !== "__skip__")} className="rounded-[4px] bg-[#fdba74] font-bold text-zinc-950 hover:bg-[#fb923c]" data-testid="receipt-apply-button">
                    {busy ? "Hazırlanıyor…" : "Fatura Taslağı Oluştur"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
